import { useEffect, useState, useMemo } from 'react';
import { Row, Col, Spinner, Badge } from 'react-bootstrap';
import DppModal from './DppModal';
import { useIntl } from 'react-intl';
import { fetchConceptDescription } from '../../services/DppManagerService';
import getLocalizedText from '../Utils';
import Config from '../../config/Config';

const DetailItem = ({ label, value, code = false }) => (
  <div className="mb-2">
    <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', marginBottom: '2px' }}>
      {label}
    </div>
    {code ? (
      <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '5px 10px', marginTop: '2px' }}>
        <code style={{ fontSize: '12px', color: '#555', wordBreak: 'break-all' }}>{value}</code>
      </div>
    ) : (
      <div style={{ fontSize: '14px', color: '#333', wordBreak: 'break-word' }}>{value}</div>
    )}
  </div>
);

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', borderBottom: '1px solid #f0f0f0', paddingBottom: '5px', marginBottom: '10px' }}>
    {children}
  </div>
);

function ElementDetailsModal({ show, onHide, element }) {
  const { mainColor } = Config.appearance;
  const intl = useIntl();

  const {
    idShort,
    modelType,
    displayName,
    description,
    value,
    semanticId,
  } = element || {};

  const semanticIdValue = useMemo(() => (
    semanticId?.keys?.[0]?.value || null
  ), [semanticId]);

  const [conceptDescription, setConceptDescription] = useState(null);
  const [loadingCD, setLoadingCD] = useState(false);
  const [errorCD, setErrorCD] = useState(null);

  useEffect(() => {
    if (!show || !semanticIdValue) {
      setConceptDescription(null);
      setErrorCD(null);
      return;
    }

    let mounted = true;
    setLoadingCD(true);
    setErrorCD(null);

    fetchConceptDescription(semanticIdValue)
      .then(cd => { if (mounted) setConceptDescription(cd); })
      .catch(err => { if (mounted) setErrorCD(err.message || 'Failed to load'); })
      .finally(() => { if (mounted) setLoadingCD(false); });

    return () => { mounted = false; };
  }, [semanticIdValue, show]);

  const getConceptDefinition = (cd) => {
    if (!cd) return '—';
    const iec61360 = cd.embeddedDataSpecifications?.find(
      spec => spec.dataSpecificationContent?.modelType === 'DataSpecificationIec61360'
    );
    const defArray = iec61360?.dataSpecificationContent?.definition;
    return defArray?.length > 0 ? getLocalizedText(defArray, '—', intl.locale) : '—';
  };

  const getConceptName = (cd) => {
    if (!cd) return null;
    const iec61360 = cd.embeddedDataSpecifications?.find(
      spec => spec.dataSpecificationContent?.modelType === 'DataSpecificationIec61360'
    );
    const nameArray = iec61360?.dataSpecificationContent?.preferredName;
    return nameArray?.length > 0 ? getLocalizedText(nameArray, null, intl.locale) : null;
  };

  const isImage = (url) => /\.(jpe?g|png|gif|webp|svg)$/i.test(url);
  const isPdf = (url) => /\.pdf$/i.test(url);

  const renderPreview = (url) => {
    if (isImage(url)) {
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img src={url} alt="Preview" className="img-fluid rounded" style={{ maxHeight: '240px' }} />
        </a>
      );
    }
    if (isPdf(url)) {
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <iframe src={url} title="PDF Preview" className="w-100 rounded" style={{ height: '300px', border: 'none' }} />
        </a>
      );
    }
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <iframe src={url} title="Website Preview" className="w-100 rounded" style={{ height: '300px', border: 'none' }} />
      </a>
    );
  };

  const t = (id, def) => intl.formatMessage({ id, defaultMessage: def });
  const modalTitle = getLocalizedText(displayName, 'N/A', intl.locale);
  const isUrl = value && typeof value === 'string' && /^https?:\/\//i.test(value);
  const hasPreview = isUrl;

  return (
    <DppModal show={show} handleClose={onHide} size="xl" centered title={modalTitle}>
      <Row className="g-3">

        {/* Preview column — only for HTTP URLs */}
        {hasPreview && (
          <Col xs={12} md={5}>
            <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '16px', minHeight: '140px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              {renderPreview(value)}
              <code style={{ fontSize: '11px', color: '#aaa', wordBreak: 'break-all', textAlign: 'center' }}>{value}</code>
            </div>
          </Col>
        )}

        {/* Details column */}
        <Col xs={12} md={hasPreview ? 7 : 12}>

          {/* Name — only if different from the modal title */}
          {displayName && getLocalizedText(displayName, null, intl.locale) !== modalTitle && (
            <DetailItem label={t('name', 'Name')} value={getLocalizedText(displayName, '—', intl.locale)} />
          )}

          {/* Description */}
          <DetailItem
            label={t('description', 'Description')}
            value={description
              ? getLocalizedText(description, '—', intl.locale)
              : t('no_description_available', 'No description available')}
          />

          {/* Value — for non-URL strings, arrays (MultiLanguageProperty), or other types */}
          {!hasPreview && value != null && (
            <>
              <hr style={{ borderColor: '#f0f0f0', margin: '4px 0 12px' }} />
              <DetailItem
                label={t('value', 'Value')}
                value={Array.isArray(value) ? getLocalizedText(value, '—', intl.locale) : String(value)}
              />
            </>
          )}

          {/* Technical Information */}
          <hr style={{ borderColor: '#f0f0f0', margin: '4px 0 12px' }} />
          <SectionLabel>{t('technical_information', 'Technical Information')}</SectionLabel>

          {idShort && <DetailItem label={t('id_short', 'ID Short')} value={idShort} />}

          {modelType && (
            <div className="mb-2">
              <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', marginBottom: '5px' }}>
                {t('type', 'Type')}
              </div>
              <Badge bg="" style={{ backgroundColor: mainColor, fontSize: '12px', fontWeight: 500 }}>
                {modelType}
              </Badge>
            </div>
          )}

          {semanticIdValue && <DetailItem label={t('semantic_id', 'Semantic ID')} value={semanticIdValue} code />}

          {/* Concept Description */}
          {semanticIdValue && (
            <>
              <hr style={{ borderColor: '#f0f0f0', margin: '4px 0 12px' }} />
              <SectionLabel>{t('concept_description', 'Concept Description')}</SectionLabel>

              {loadingCD && (
                <div className="d-flex align-items-center gap-2 text-muted mb-2" style={{ fontSize: '13px' }}>
                  <Spinner animation="border" size="sm" />
                  <span>{t('loading', 'Loading...')}</span>
                </div>
              )}

              {errorCD && (
                <p style={{ fontSize: '13px', color: '#aaa' }}>{t('no_concept_description_found', 'No concept description found')}</p>
              )}

              {!loadingCD && !errorCD && conceptDescription && (
                <>
                  {getConceptName(conceptDescription) && (
                    <DetailItem label={t('official_name', 'Official Name')} value={getConceptName(conceptDescription)} />
                  )}
                  <DetailItem label={t('definition', 'Definition')} value={getConceptDefinition(conceptDescription)} />
                </>
              )}

              {!loadingCD && !errorCD && !conceptDescription && (
                <p style={{ fontSize: '13px', color: '#aaa' }}>—</p>
              )}
            </>
          )}

        </Col>
      </Row>
    </DppModal>
  );
}

export default ElementDetailsModal;
